import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../../config/apiClientConfig';

const answerSchema = z.object({
	id: z.number(),
	questionId: z.number(),
	answerText: z.string(),
	answerParentId: z.number().nullish(),
	isCustomAnswer: z.boolean(),
});

export type AnswerTypeSchema = z.infer<typeof answerSchema>;

export enum QuestionTypes {
	BooleanQuestion = 1,
	YearPicker = 2,
	RadioButton = 3,
	CheckBox = 4,
	DatePicker = 6,
}

const questionSchema = z.object({
	id: z.number(),
	questionText: z.string(),
	dataTypeId: z.number(),
	parentQuestionId: z.number().nullish(),
	answers: z.array(answerSchema),
	savedAnswerId: z.number().nullish(),
	savedAnswer: z.string().nullish(),
});

const memberQuestionsSchema = z.object({
	memberId: z.string(),
	isMedicalConditionDeclared: z.boolean().nullish(),
	height: z.number(),
	weight: z.number(),
	questions: z.array(questionSchema),
});

type Member = {
	memberId: string;
	isMedicalConditionDeclared: boolean | null;
	height: number;
	weight: number;
	questions: {
		id: number;
		questionText: string;
		dataTypeId: number;
		parentQuestionId: number | null;
	}[];
	answers: {
		id: number;
		questionId: number;
		answerText: string;
		isCustomAnswer: boolean;
		answerParentId: number | null;
	}[];
	savedMedicalAnswers: {
		id: number;
		memberId: string;
		questionId: number;
		answerId?: number;
		answer?: string | null;
	}[];
};

const transformMemberData = (member: Member) => {
	const { questions, answers, savedMedicalAnswers, ...rest } = member;

	const finalQuestions = questions.map((q) => {
		return {
			...q,
			answers: answers.filter((v) => v.questionId === q.id).sort((a, b) => a.id - b.id),
			savedAnswerId: savedMedicalAnswers.find((v) => v.questionId === q.id)?.answerId ?? null,
			savedAnswer: savedMedicalAnswers.find((v) => v.questionId === q.id)?.answer ?? null,
		};
	});

	return { ...rest, questions: finalQuestions };
};

const getMedicalDeclarationQuestions = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/Member/:journeyId/medical-declaration-questions',
		method: 'GET',
		errorCodes: ['InvalidJounreyId'],
		responseSchema: z.array(memberQuestionsSchema),
		requestParameters: { journeyId: ['required', 'string'] },
		transformResponse: (res: { memberQuestions: Member[] }) => {
			return res.memberQuestions.map((mq) => transformMemberData(mq));
		},
	});

export default getMedicalDeclarationQuestions;

export type MedicalDeclarationQuestionsResponse = TypeOfAPIResponse<ReturnType<typeof getMedicalDeclarationQuestions>>;

export type MedicalDeclarationQuestionsError = TypeOfAPIError<ReturnType<typeof getMedicalDeclarationQuestions>>;

export type QuestionTypeSchema = z.infer<typeof questionSchema>;
